<template>
	<div>
		<div class="stage">
			<div class="row margin-b20">
				<div class="col-xs-12 col-sm-6 col-md-6 col-lg-6">
					<back-button to="/carriers" />
				</div>
				<div class="col-xs-12 col-sm-6 col-md-6 col-lg-6">
					<action-buttons
						:disabled="
							!editCarrier.name ||
							!editCarrier.transport ||
							!editCarrier.port ||
							!editCarrier.server ||
							!editCarrier.tenant ||
							!editCarrier.from_number_type ||
							!editCarrier.to_number_type ||
							(editCarrier.requires_auth && !editCarrier.username && !editCarrier.password) ||
							loading
						"
						:save-label="editCarrier.id ? 'Save' : 'Create'"
						@save="save"
						@close="routeTo('/carriers')"
					/>
				</div>
			</div>

			<div class="row margin-b10">
				<div class="col-xs-12 col-sm-12 col-md-12 col-lg-6 col-lg-offset-3">
					<card-wrapper :loading="loading">
						<template #default>
							<div class="mx-auto mb-5 w-full">
								<div class="col-sm-12 textCenter">
									<div class="navAvatar margin-b10 flex w-full justify-center">
										<img src="/assets/images/teleflow-logo-black.png" width="200" />
									</div>
								</div>
							</div>

							<form-section title="Carrier">
								<form-field label="Name" class="w-full px-2">
									<form-kit
										v-model="editCarrier.name"
										type="text"
										class="form-control"
										placeholder="Name"
										name="Name"
										:validation="'required'"
										validation-visibility="live"
									/>
								</form-field>

								<form-field
									v-if="
										!editCarrier.id &&
										(tenants?.length > 1 || $global.hasUserRole(userRoles, 'reseller'))
									"
									label="Tenant"
									has-icon
									icon-class="fa-chevron-down"
									class="w-full px-2"
								>
									<select v-model="editCarrier.tenant">
										<option disabled :value="null">-- Select Tenant --</option>
										<option v-for="t in tenants" :key="t.id" :value="t">
											{{ t.companyName }}
										</option>
									</select>
								</form-field>

								<form-field label="Server Address" class="w-full px-2">
									<form-kit
										v-model="editCarrier.server"
										type="text"
										class="form-control"
										placeholder="Server Address"
										name="Server Address"
										:validation="'required'"
										validation-visibility="live"
									/>
								</form-field>

								<div class="mb-2 flex w-full flex-row items-center justify-center md:mb-5">
									<form-field label="Port" class="w-1/2 px-2">
										<form-kit
											v-model="editCarrier.port"
											type="number"
											class="form-control"
											name="Port"
											:validation="'required'"
											validation-visibility="live"
										/>
									</form-field>
									<form-field label="Prefix (optional)" class="w-1/2 px-2">
										<form-kit
											v-model="editCarrier.prefix"
											type="number"
											class="form-control"
											placeholder="Prefix"
										/>
									</form-field>
								</div>

								<div class="mb-2 flex w-full flex-row items-center justify-center md:mb-5">
									<form-field label="Requires Auth?" class="w-1/2 px-2">
										<input
											v-model="editCarrier.requires_auth"
											type="checkbox"
											class="tw-toggle ml-2"
										/>
									</form-field>
									<form-field label="Bypass Media?" class="w-1/2 px-2">
										<input
											v-model="editCarrier.bypass_media"
											type="checkbox"
											class="tw-toggle ml-2"
										/>
									</form-field>
								</div>

								<template v-if="editCarrier.requires_auth">
									<form-field label="Username" class="w-full px-2">
										<form-kit
											v-model="editCarrier.username"
											type="text"
											class="form-control"
											placeholder="Username"
										/>
									</form-field>

									<form-field label="Password" class="w-full px-2">
										<form-kit
											v-model="editCarrier.password"
											type="password"
											autocomplete="new-password"
											class="form-control"
											placeholder="Password"
										/>
									</form-field>
								</template>

								<form-field label="Proxy (optional)" class="w-full px-2">
									<form-kit
										v-model="editCarrier.proxy"
										type="text"
										class="form-control"
										placeholder="Proxy"
									/>
								</form-field>
							</form-section>

							<form-section title="Network">
								<form-field
									label="Network Transport"
									has-icon
									icon-class="fa-chevron-down"
									class="w-full px-2"
								>
									<form-kit
										v-model="editCarrier.transport"
										type="select"
										name="Network Transport"
										:validation="'required'"
										validation-visibility="live"
									>
										<option disabled value="">-- Select network transport --</option>
										<option value="tcp">TCP</option>
										<option value="udp">UDP</option>
										<option value="tls">TLS</option>
									</form-kit>
								</form-field>

								<div class="mb-2 flex w-full flex-row flex-wrap justify-center md:mb-5">
									<form-field
										label="From Number Type"
										has-icon
										icon-class="fa-chevron-down"
										class="w-1/2 px-2"
									>
										<form-kit
											v-model="editCarrier.from_number_type"
											type="select"
											name="From Number"
											:validation="'required'"
											validation-visibility="live"
										>
											<option disabled value="">-- Select from number type --</option>
											<option value="E164">E164</option>
											<option value="E164+">E164+</option>
											<option value="E164_00">E164 UK, 00 International</option>
											<option value="DOMESTIC">Domestic</option>
											<option value="NATIONAL">National, no leading zero</option>
										</form-kit>
									</form-field>

									<form-field
										label="To Number Type"
										has-icon
										icon-class="fa-chevron-down"
										class="w-1/2 px-2"
									>
										<form-kit
											v-model="editCarrier.to_number_type"
											type="select"
											name="To Number"
											:validation="'required'"
											validation-visibility="live"
										>
											<option disabled value="">-- Select to number type --</option>
											<option value="E164">E164</option>
											<option value="E164+">E164+</option>
											<option value="E164_00">E164 UK, 00 International</option>
											<option value="DOMESTIC">Domestic</option>
											<option value="NATIONAL">National, no leading zero</option>
										</form-kit>
									</form-field>
								</div>

								<form-field label="Pass-through Custom Headers" class="px-2">
									<div class="my-2 mb-2">
										<button class="fab fabSm green" @click="addProxyHeader">
											<span class="fa-light fa-plus"></span>
										</button>
									</div>
								</form-field>

								<div
									v-for="(_, i) in editCarrier.proxy_headers"
									:key="i"
									class="margin-t10 mb-5 w-full"
								>
									<div class="flex w-full flex-row">
										<div class="w-full md:w-1/2">
											<form-field class="w-full px-2">
												<form-kit
													v-model="editCarrier.proxy_headers[i]"
													type="text"
													class="form-control"
													placeholder="Enter Header Name"
												/>
											</form-field>
										</div>
										<div v-if="editCarrier.proxy_headers?.length > 1" class="px-2">
											<button class="fab fabSm red" @click="removeProxyHeader(i)">
												<span class="fa-light fa-minus"></span>
											</button>
										</div>
									</div>
								</div>

								<form-field label="Add Custom Headers" class="px-2">
									<div class="my-2 mb-2">
										<button class="fab fabSm green" @click="addCustomHeader">
											<span class="fa-light fa-plus"></span>
										</button>
									</div>
								</form-field>

								<div class="formGroup mb-2 px-2 md:mb-3">
									<span class="fa-light fa-circle-info"> </span>
									<label class="textGrey ml-2"
										>Only "X-" header names supported. Please omit the "X-" from the name field
										below, this will be auto-added</label
									>
								</div>

								<div
									v-for="(_, i) in editCarrier.custom_headers"
									:key="i"
									class="margin-t10 mb-5 w-full"
								>
									<div class="flex w-full flex-row">
										<div class="w-full md:w-1/2">
											<form-field class="w-full px-2">
												<form-kit
													v-model="editCarrier.custom_headers[i].name"
													type="text"
													class="form-control"
													placeholder="Name"
												/>
											</form-field>
										</div>
										<div class="w-full md:w-1/2">
											<form-field class="w-full px-2">
												<form-kit
													v-model="editCarrier.custom_headers[i].value"
													:type="editCarrier.custom_headers[i].type || 'text'"
													class="form-control"
													placeholder="Value"
												/>
											</form-field>
										</div>
										<div v-if="editCarrier.custom_headers?.length > 1" class="px-2">
											<button class="fab fabSm red" @click="removeCustomHeader(i)">
												<span class="fa-light fa-minus"></span>
											</button>
										</div>
									</div>
								</div>
							</form-section>
							<div class="row items-center justify-center">
								<action-buttons
									:disabled="
										!editCarrier.name ||
										!editCarrier.transport ||
										!editCarrier.port ||
										!editCarrier.server ||
										!editCarrier.tenant ||
										!editCarrier.from_number_type ||
										!editCarrier.to_number_type ||
										(editCarrier.requires_auth && !editCarrier.username && !editCarrier.password) ||
										loading
									"
									:save-label="editCarrier.id ? 'Save' : 'Create'"
									@save="save"
									@close="routeTo('/carriers')"
								/>
							</div>
						</template>
					</card-wrapper>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import carriersApi from '../scripts/carriers/carriers.api.js'
import { router } from '../router'

export default {
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			editCarrier: {
				name: '',
				tenant: null,
				dtmf: null,
				codecs: [],
				transport: null,
				prefix: '',
				port: 5060,
				bypass_media: true,
				requires_auth: false,
				username: null,
				password: null,
				to_number_type: undefined,
				from_number_type: undefined,
				server: '',
				proxy: null,
				proxy_headers: [''],
				custom_headers: [{}]
			},
			loading: true,
			supportedCodecs: ['PCMA', 'PCMU', 'G722', 'OPUS']
		}
	},

	async created() {
		try {
			if (this.$route.params.id !== 'new') {
				const result = await carriersApi.getById(this.$route.params.id)
				this.editCarrier = result.data
				this.loading = false
				if (!this.editCarrier.proxy_headers?.length) {
					this.editCarrier.proxy_headers = ['']
				}
				if (!this.editCarrier.custom_headers?.length) {
					this.editCarrier.custom_headers = [{}]
				}
			} else if (this.tenants?.length === 1) {
				this.editCarrier.tenant = this.tenants[0]
			}

			this.loading = false
		} catch (e) {
			this.$swal({
				title: 'Error loading carrier',
				icon: 'error',
				confirmButtonText: 'Confirm',
				text: 'Unknown error occurred, please try again. If the issue persists, please raise a support ticket.'
			}).then(() => {
				router.push('/carriers')
			})
			console.error(e)
		}
	},

	methods: {
		removeProxyHeader(i) {
			this.editCarrier.proxy_headers.splice(i, 1)
		},

		addProxyHeader() {
			this.editCarrier.proxy_headers.push('')
		},

		removeCustomHeader(i) {
			this.editCarrier.custom_headers.splice(i, 1)
		},

		addCustomHeader() {
			this.editCarrier.custom_headers.push({})
		},

		routeTo(routeName) {
			router.push(routeName)
		},

		save() {
			if (!this.editCarrier.id) {
				carriersApi
					.create(this.editCarrier)
					.then(() => {
						this.routeTo('/carriers')
					})
					.catch(console.error)
			} else {
				carriersApi
					.update(this.editCarrier)
					.then(() => {
						this.routeTo('/carriers')
					})
					.catch(console.error)
			}
		}
	}
}
</script>
